/**
 * Service for managing browser notifications
 * Handles permission requests and sending notifications even when tab is not focused
 */
class NotificationService {
    constructor() {
        this.permission = 'default';
        this.checkPermission();
    }

    /**
     * Check current notification permission
     */
    checkPermission() {
        if (!('Notification' in window)) {
            console.warn('Browser does not support notifications');
            return false;
        }
        this.permission = Notification.permission;
        return this.permission === 'granted';
    }

    /**
     * Request permission to show notifications
     */
    async requestPermission() {
        if (!('Notification' in window)) {
            console.warn('Browser does not support notifications');
            return false;
        }

        if (this.permission === 'granted') {
            return true;
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    /**
     * Show a browser notification
     * @param {string} title - Notification title
     * @param {object} options - Notification options
     * @param {string} options.body - Notification body text
     * @param {string} options.icon - Icon URL
     * @param {string} options.badge - Badge URL
     * @param {string} options.tag - Notification tag (for grouping/replacing)
     * @param {boolean} options.requireInteraction - Keep notification until user interacts
     * @param {Function} options.onClick - Click handler
     */
    async show(title, options = {}) {
        // Check permission
        if (!this.checkPermission()) {
            // Try to request permission
            const granted = await this.requestPermission();
            if (!granted) {
                console.log('Notification permission denied');
                return null;
            }
        }

        try {
            const notification = new Notification(title, {
                body: options.body || '',
                icon: options.icon || '/logo192.png',
                badge: options.badge || '/logo192.png',
                tag: options.tag || 'default',
                requireInteraction: options.requireInteraction || false,
                silent: false,
                vibrate: [200, 100, 200]
            });

            // Add click handler
            if (options.onClick) {
                notification.onclick = options.onClick;
            } else {
                // Default: focus the window
                notification.onclick = () => {
                    window.focus();
                    notification.close();
                };
            }

            // Auto-close after 5 seconds if not requireInteraction
            if (!options.requireInteraction) {
                setTimeout(() => notification.close(), 5000);
            }

            return notification;
        } catch (error) {
            console.error('Error showing notification:', error);
            return null;
        }
    }

    /**
     * Show sync completion notification
     */
    async showSyncComplete(success, failed, total) {
        if (total === 0) return;

        let title, body, icon;

        if (failed === 0) {
            // All successful
            title = 'JHA Sync Complete';
            body = `Successfully sent ${success} initial report${success > 1 ? 's' : ''}`;
            icon = '/logo192.png';
        } else if (success === 0) {
            // All failed
            title = 'JHA Sync Failed';
            body = `Failed to send ${failed} initial report${failed > 1 ? 's' : ''}. Will retry when online.`;
            icon = '/logo192.png';
        } else {
            // Partial
            title = 'JHA Sync Partially Complete';
            body = `Sent ${success} of ${total} initial reports. ${failed} failed.`;
            icon = '/logo192.png';
        }

        return this.show(title, {
            body,
            icon,
            tag: 'jha-sync-complete',
            requireInteraction: false,
            onClick: () => {
                window.focus();
                // Navigate to JHA page if not already there
                if (!window.location.pathname.includes('/jha')) {
                    window.location.href = '/jha';
                }
            }
        });
    }

    /**
     * Show sync start notification (only if tab is not visible)
     */
    async showSyncStart(total) {
        // Only show if document is hidden (user is on another tab)
        if (!document.hidden) {
            return null;
        }

        return this.show('JHA Sync Started', {
            body: `Sending ${total} pending initial report${total > 1 ? 's' : ''}...`,
            tag: 'jha-sync-start',
            requireInteraction: false
        });
    }
}

export default new NotificationService();
